import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import QuizIcon from "@mui/icons-material/Quiz";

export default function CourseCurriculum({ modules = [],
  activeLessonId,
  onLessonSelect,
  onAssessmentSelect,
}) {
  const theme = useTheme();

  return (
    <Box sx={{ background: theme.palette.background.paper, p: 2 }}>
      <Typography fontSize={21} fontWeight={400} mb={2} color={theme.palette.text.primary}>
        Course Content
      </Typography>

      {modules.map((module) => (
        <Accordion
          key={module.id}
          disableGutters
          elevation={0}
          sx={{
            mb: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            "&:before": { display: "none" },
          }}
        >
          {/* MODULE */}
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontSize={18} fontWeight={400} color={theme.palette.text.secondary}>
              {module.title}
            </Typography>
          </AccordionSummary>

          <AccordionDetails sx={{ px: 0 }}>
            {module.topics?.length === 0 && (
              <Typography
                fontSize={14}
                color={theme.palette.text.secondary}
                sx={{ px: 2, py: 1 }}
              >
                No topics added
              </Typography>
            )}

            {module.topics?.map((topic) => (
              <Accordion
                key={topic.id}
                disableGutters
                elevation={0}
                sx={{
                  mb: 1,
                  borderRadius: "6px",
                  "&:before": { display: "none" },
                }}
              >
                {/* TOPIC */}
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontSize={16} fontWeight={400} color={theme.palette.text.primary}>
                    {topic.title}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ px: 0 }}>
                  {topic.subTopics?.length === 0 && (
                    <Typography
                      fontSize={14}
                      color={theme.palette.text.secondary}
                      sx={{ px: 3, py: 1 }}
                    >
                      No lessons added
                    </Typography>
                  )}

                  {/* SUB-TOPICS */}
                  {topic.subTopics?.map((sub) => {
                    const hasAssessment =
                      sub.assessment?.questions?.some((q) => {
                        const hasQuestion =
                          q.question?.html?.trim() ||
                          q.question?.imageUrl ||
                          q.question?.pdfUrl ||
                          q.question?.attachmentUrl;

                        const hasOption = q.options?.some(
                          (opt) =>
                            opt.html?.trim() ||
                            opt.imageUrl ||
                            opt.attachmentUrl
                        );

                        return hasQuestion && hasOption;
                      });

                    return (
                      <Box key={sub.id}>
                        {/* LESSON */}
                        <Box
                          onClick={() => {
                            if (sub.video) {
                              onLessonSelect(sub);
                            }
                          }}
                          sx={{
                            cursor: sub.video ? "pointer" : "not-allowed",
                            p: 2,
                            mb: hasAssessment ? 0.5 : 1,
                            ml: 2,
                            borderRadius: "8px",
                            borderLeft: `4px solid ${sub.id === activeLessonId
                                ? theme.palette.primary.main
                                : theme.palette.divider
                              }`,
                            background:
                              sub.id === activeLessonId
                                ? theme.palette.action.selected
                                : theme.palette.background.paper,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            opacity: sub.video ? 1 : 0.5,
                            boxShadow: `0 1px 2px ${theme.palette.action.hover}`,
                          }}
                        >
                          <Box>
                            <Typography
                              fontSize={15}
                              fontWeight={400}
                              color={theme.palette.text.primary}
                            >
                              {sub.title}
                            </Typography>

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <AccessTimeIcon
                                style={{
                                  fontSize: 16,
                                  color: theme.palette.text.secondary,
                                }}
                              />

                              <Typography
                                fontSize={13}
                                color={theme.palette.text.secondary}
                              >
                                {sub.duration || "—"}
                              </Typography>
                            </Box>
                          </Box>

                          {sub.video ? (
                            <VisibilityIcon
                              sx={{
                                color: theme.palette.primary.main,
                              }}
                            />
                          ) : (
                            <VisibilityOffIcon
                              sx={{
                                color: theme.palette.text.secondary,
                              }}
                            />
                          )}
                        </Box>

                        {/* ASSESSMENT */}
                        {hasAssessment && (
                          <Box
                            onClick={() => {
                              onAssessmentSelect?.(sub);
                            }}
                            sx={{
                              ml: 5,
                              mr: 1,
                              mb: 1,
                              px: 2,
                              py: 1.5,
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              cursor: "pointer",
                              backgroundColor: theme.palette.action.hover,
                              border: `1px solid ${theme.palette.divider}`,
                              transition: "all 0.2s ease",

                              "&:hover": {
                                backgroundColor: theme.palette.action.selected,
                                transform: "translateX(3px)",
                              },
                            }}
                          >
                            <QuizIcon
                              sx={{
                                fontSize: 20,
                                color: theme.palette.primary.main,
                              }}
                            />

                            <Box>
                              <Typography
                                fontSize={14}
                                fontWeight={500}
                                color={theme.palette.text.primary}
                              >
                                Assessment
                              </Typography>

                              <Typography
                                fontSize={12}
                                color={theme.palette.text.secondary}
                              >
                                Test your understanding
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
